import {Injectable} from '@angular/core';
import {first} from "rxjs/operators";
import {Observable} from "rxjs";
import {Result} from "@domain/common/http/result";
import {environment} from "@environments/environment";
import {HttpClient} from "@angular/common/http";
import {Balise} from "@domain/admin/bibliotheque/notification/balise";
import {MessageAdmin} from "@domain/admin/bibliotheque/notification/message-admin";
import {ConfigSMTP} from "@domain/admin/bibliotheque/notification/config-smtp";

/**
 * Service du menu notification
 *
 * @author Lionel Gimenez
 * @date 12/03/2026
 */
@Injectable({
    providedIn: "root",
})
export class NotificationService {
    /**
     * Constructeur
     */
    constructor(private http: HttpClient) {}

    /**
     * Suppression de la balise
     *
     * @param idBalise l'identifiant de la balise à supprimer
     */
    deleteBalise(idBalise: number): Observable<Result> {
        return this.http.delete<Result>(
                `${environment.baseUrl}/controller/Notification/deleteBalise/${idBalise}`)
                .pipe(first());
    }

    /**
     * Chargement d'une balise
     *
     * @param idBalise l'identifiant de la balise à charger
     */
    loadBalise(idBalise: number): Observable<Result> {
        return this.http.get<Result>(
                `${environment.baseUrl}/controller/Notification/getBaliseNotification/${idBalise}`)
                .pipe(first());
    }

    /**
     * Sauvegarde d'une balise
     *
     * @param balise la balise à sauvegarder
     */
    saveBalise(balise: Balise): Observable<Result> {
        return this.http.post<Result>(
                `${environment.baseUrl}/controller/Notification/saveBalise`, balise)
                .pipe(first());
    }

    /**
     * Suppression de rôles d'un message admin
     *
     * @param idMessage l'identifiant du message
     * @param listeRoleId la liste des identifiants de rôles à supprimer
     */
    deleteRoles(idMessage: number, listeRoleId: number[]): Observable<Result> {
        return this.http.post<Result>(
            `${environment.baseUrl}/controller/MessageAdmin/deleteRoles/${idMessage}`, listeRoleId)
            .pipe(first());
    }

    /**
     * Sauvegarde de rôles d'un message admin
     *
     * @param idMessage l'identifiant du message
     * @param listeRoleId la liste des identifiants de rôles à sauvegarder
     */
    saveRoles(idMessage: number, listeRoleId: number[]): Observable<Result> {
        return this.http.post<Result>(
            `${environment.baseUrl}/controller/MessageAdmin/saveRoles/${idMessage}`, listeRoleId)
            .pipe(first());
    }

    /**
     * Chargement d'un message admin
     *
     * @param idMessage l'identifiant du message à charger
     */
    loadMessageAdmin(idMessage: number): Observable<Result> {
        return this.http.get<Result>(
            `${environment.baseUrl}/controller/MessageAdmin/loadMessage/${idMessage}`)
            .pipe(first());
    }

    /**
     * Sauvegarde d'un message admin
     *
     * @param messageAdmin Message à sauvegarder
     */
    saveMessageAdmin(messageAdmin: MessageAdmin): Observable<Result> {
        return this.http.put<Result>(
            `${environment.baseUrl}/controller/MessageAdmin/saveMessage`, messageAdmin)
            .pipe(first());
    }

    /**
     * Suppression d'un message
     *
     * @param idMessage l'identifiant du message à supprimer
     */
    deleteMessage(idMessage: number): Observable<Result> {
        return this.http.delete<Result>(
            `${environment.baseUrl}/controller/MessageAdmin/deleteMessage/${idMessage}`)
            .pipe(first());
    }

    /**
     * Duplication d'un message
     *
     * @param idMessage l'identifiant du message à dupliquer
     */
    duplicateMessage(idMessage: number): Observable<Result> {
        return this.http.post<Result>(
            `${environment.baseUrl}/controller/MessageAdmin/dupliquerMessage/${idMessage}`, null)
            .pipe(first());
    }

    /**
     * Envoi du message par email
     *
     * @param idMessage l'identifiant du message à envoyer par email
     */
    sendEmail(idMessage: number): Observable<Result> {
        return this.http.post<Result>(
            `${environment.baseUrl}/controller/MessageAdmin/sendEmail/${idMessage}`, null)
            .pipe(first());
    }

    /**
     * Chargement de la configuration SMTP
     */
    loadConfigSMTP(): Observable<Result> {
        return this.http.post<Result>(
            `${environment.baseUrl}/controller/ConfigSMTP/consultConfig`, null)
            .pipe(first());
    }

    /**
     * Sauvegarde de la configuration SMTP
     *
     * @param configSMTP la configuration à sauvegarder
     */
    saveConfigSMTP(configSMTP: ConfigSMTP): Observable<Result> {
        return this.http.post<Result>(
            `${environment.baseUrl}/controller/ConfigSMTP/saveConfig`, configSMTP)
            .pipe(first());
    }
}
